/**
 * Trade marker view preferences.
 *
 * Two flags, and they are deliberately separate concerns:
 *
 *  - `enabled` — whether the chart draws the trades you actually took at all.
 *    Off means genuinely off: nothing drawn, nothing clickable, and the
 *    30-second `/api/trade-transactions` poll never starts. See
 *    `useTradeMarkerFeed`.
 *  - `showLinks` — whether the faint dotted entry-to-exit connectors are drawn.
 *    Purely a line preference. It never hides a marker: turning the lines off
 *    on a busy chart must not quietly take the exits with them, or the setting
 *    would be doing two things and only naming one.
 *
 * `showLinks` is meaningless while `enabled` is false, and nothing enforces
 * that — it does not need to. The primitive is handed no markers at all in that
 * state, so there are no pairs to join and the flag has nothing to act on.
 *
 * Persisted to localStorage, matching every other chart toggle in the action
 * bar (`fairPrice.enabled`, `displacement.enabled`, `setupCandidates.enabled`).
 * The diamonds moved their THRESHOLDS server-side because the bot has to score
 * with the same rule the chart draws with; visibility is a per-browser view
 * preference with no engine counterpart, so it stays local — same as the
 * diamonds' own on/off flag.
 *
 * Both default ON. The markers were unconditional before the `enabled` toggle
 * existed, so off-by-default would silently remove a feature from everyone who
 * upgrades; the connectors ship with the exits and are the reason the exits are
 * legible as pairs, so they start on for the same reason.
 */

import { create } from 'zustand';

const ENABLED_STORAGE_KEY = 'tradeMarkers.enabled';
const SHOW_LINKS_STORAGE_KEY = 'tradeMarkers.showLinks';

/**
 * Absent key means never toggled — fall back to the default rather than false.
 *
 * Loose `== null` on purpose, so `undefined` counts as absent too. The spec says
 * getItem returns null, but shimmed storage does not always: the test harness's
 * own stub returns undefined, and a strict `=== null` there would resolve to
 * `raw === 'true'` — false — quietly turning the feature off for a user who had
 * never touched the toggle. That is precisely the failure the default prevents.
 */
function loadPersistedBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? fallback : raw === 'true';
  } catch {
    return fallback; // SSR / storage blocked
  }
}

function persistBool(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // storage blocked — non-fatal
  }
}

interface TradeMarkerStoreState {
  /** Marker visibility (persisted to localStorage). */
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** Entry-to-exit connector visibility (persisted to localStorage). */
  showLinks: boolean;
  setShowLinks: (showLinks: boolean) => void;
}

export const useTradeMarkerStore = create<TradeMarkerStoreState>((set) => ({
  enabled: loadPersistedBool(ENABLED_STORAGE_KEY, true),

  setEnabled: (enabled) => {
    persistBool(ENABLED_STORAGE_KEY, enabled);
    set({ enabled });
  },

  showLinks: loadPersistedBool(SHOW_LINKS_STORAGE_KEY, true),

  setShowLinks: (showLinks) => {
    persistBool(SHOW_LINKS_STORAGE_KEY, showLinks);
    set({ showLinks });
  },
}));

/**
 * Mirrors `useCandidatesEnabled` — a plain boolean, never a new object.
 *
 * Both of these return a PRIMITIVE, and that is not a style preference. A
 * selector that built an object or array would return a fresh one on every
 * call, compare unequal under zustand's `Object.is`, and re-render forever —
 * React error #185, which has taken this chart down twice.
 */
export const useTradeMarkersEnabled = (): boolean =>
  useTradeMarkerStore((s) => s.enabled);

/** Whether the entry-to-exit connectors are drawn. A plain boolean, as above. */
export const useTradeMarkerLinksEnabled = (): boolean =>
  useTradeMarkerStore((s) => s.showLinks);

/** Exported for tests, so the keys live in exactly one place. */
export const TRADE_MARKERS_ENABLED_KEY = ENABLED_STORAGE_KEY;
export const TRADE_MARKERS_SHOW_LINKS_KEY = SHOW_LINKS_STORAGE_KEY;
